import round from 'lodash/round';

import {
  convertMillisToUnit,
  normalizeMillis,
  TimeUnit,
  toNumOrUndefined,
} from '@cast/utils';

import { ReliabilityMetricKey } from '../_constants/reliabilityMetrics';

const LATENCY_METRIC_KEYS: ReliabilityMetricKey[] = [
  'latencyP50',
  'latencyP95',
  'latencyP99',
];

type FormattedLatency = {
  value: number;
  unit: TimeUnit;
};

export const isLatencyMetricKey = (
  metricKey: ReliabilityMetricKey
): boolean => {
  return LATENCY_METRIC_KEYS.includes(metricKey);
};

export const formatLatency = (
  value: number | string | null | undefined,
  roundBy = 2
): FormattedLatency | undefined => {
  const num = toNumOrUndefined(value);
  if (num === undefined) {
    return undefined;
  }

  const normalized = normalizeMillis(num, { roundingBreakpoint: roundBy });
  return {
    value: round(normalized.value, roundBy),
    unit: normalized.unit,
  };
};

export const formatLatencyWithUnit = (
  value: number | string | null | undefined,
  roundBy = 2,
  dashes = '--.-- ms'
): string => {
  const formatted = formatLatency(value, roundBy);
  return formatted ? `${formatted.value} ${formatted.unit}` : dashes;
};

export const buildLatencyTickFormatter = (
  chartData: Array<Record<string, unknown>>,
  metricKeys: string[]
): ((tick: number | string) => string) => {
  const maxValue = Math.max(
    0,
    ...chartData.flatMap((d) =>
      metricKeys.map((k) => toNumOrUndefined(d[k] as number | string) ?? 0)
    )
  );
  const { unit } = normalizeMillis(maxValue, { maxGranularity: 's' });

  return (tick) => {
    const num = toNumOrUndefined(tick);
    if (num === undefined) return String(tick);
    return `${round(convertMillisToUnit(num, unit), 2)} ${unit}`;
  };
};
